import { isDefined } from "@dhx/ts-common/core";
import { inject, el } from "@dhx/ts-common/dom";
import { ScrollView } from "@dhx/ts-common/ScrollView";
import { Cell } from "./Cell";
import { ILayout, ICellConfig, ILayoutConfig, IProCell } from "./types";

export class ProCell extends Cell implements IProCell {
	public scrollView: ScrollView;

	constructor(parent: string | HTMLElement | ILayout, config: ICellConfig) {
		super(parent, config);

		this.scrollView = new ScrollView(() => {
			return this._getFirstRootView();
		});
	}

	private _getFirstRootView(self = this) {
		return self.getParent() && self.getParent().getRootView()
			? self.getParent().getRootView()
			: this._getFirstRootView((self as any).getParent());
	}

	toVDOM(nodes?: any[]) {
		if (this.config === null) {
			this.config = {};
		}
		if (this.config.hidden) {
			return;
		}

		const style = this._calculateStyle();
		const stylePadding = isDefined(this.config.padding)
			? !isNaN(Number(this.config.padding))
				? { padding: `${this.config.padding}px` }
				: { padding: this.config.padding }
			: "";
		const fullStyle = this.config.full || this.config.html ? style : { ...style, ...stylePadding };

		let kids;
		if (!this.config.html) {
			if (this._ui) {
				let view = this._ui.getRootView();
				if (view.render) {
					view = inject(view);
				}
				// kids = [view];
				kids = view ? [this.scrollView.render(view)] : view || null;
			} else {
				// kids = nodes || null;
				kids = nodes ? this.scrollView.render([nodes]) : nodes || null;
			}
		}
		const resizer =
			this.config.resizable && !this._isLastCell() && this._getNextCell() && !this.config.collapsed
				? el(
						".dhx_layout-resizer." +
							(this._isXDirection() ? "dhx_layout-resizer--x" : "dhx_layout-resizer--y"),
						{
							...this._resizerHandlers,
							_ref: "resizer_" + this._uid,
						},
						[
							el("span.dhx_layout-resizer__icon", {
								class:
									"dxi " +
									(this._isXDirection() ? "dxi-dots-vertical" : "dxi-dots-horizontal"),
							}),
						]
				  )
				: null;

		const handlers = {};
		if (this.config.on) {
			for (const key in this.config.on) {
				handlers["on" + key] = this.config.on[key];
			}
		}

		let typeClass = "";
		const isParent = (this.config as any).cols || (this.config as any).rows;
		if (this.config.type && isParent) {
			switch (this.config.type) {
				case "line":
					typeClass = " dhx_layout-line";
					break;
				case "wide":
					typeClass = " dhx_layout-wide";
					break;
				case "space":
					typeClass = " dhx_layout-space";
					break;
				default:
					break;
			}
		}

		const htmlContent = el(
			".dhx_layout-cell-content",
			{
				_key: `${this._uid}_html`,
				style: stylePadding,
			},
			[
				el(".dhx_layout-cell-inner_html", {
					".innerHTML": this.config.html,
				}),
			]
		);

		const cell = el(
			"div",
			{
				_key: this._uid,
				_ref: this._uid,
				["aria-label"]: this.config.id ? "tab-content-" + this.config.id : null,
				...handlers,
				class:
					this._getCss(false) +
					(this.config.css ? " " + this.config.css : "") +
					(this.config.collapsed ? " dhx_layout-cell--collapsed" : "") +
					(this.config.resizable ? " dhx_layout-cell--resizable" : "") +
					(this.config.type && !this.config.full ? typeClass : ""),
				style: fullStyle,
			},
			this.config.full
				? [
						el(
							"div",
							{
								tabindex: this.config.collapsable ? "0" : "-1",
								class:
									"dhx_layout-cell-header" +
									(this._isXDirection()
										? " dhx_layout-cell-header--col"
										: " dhx_layout-cell-header--row") +
									(this.config.collapsable ? " dhx_layout-cell-header--collapseble" : "") +
									(this.config.collapsed ? " dhx_layout-cell-header--collapsed" : "") +
									(((this.getParent() || ({} as any)).config || ({} as any)).isAccordion
										? " dhx_layout-cell-header--accordion"
										: ""),
								style: {
									height: this.config.headerHeight,
								},
								onclick: this._handlers.toggle,
								onkeydown: this._handlers.enterCollapse,
							},
							[
								this.config.headerIcon &&
									el("span.dhx_layout-cell-header__icon", {
										class: this.config.headerIcon,
									}),
								this.config.headerImage &&
									el(".dhx_layout-cell-header__image-wrapper", [
										el("img", {
											src: this.config.headerImage,
											class: "dhx_layout-cell-header__image",
										}),
									]),
								this.config.header &&
									el("h3.dhx_layout-cell-header__title", this.config.header),
								this.config.collapsable
									? el("div.dhx_layout-cell-header__collapse-icon", {
											class: this._getCollapseIcon(),
									  })
									: el("div.dhx_layout-cell-header__collapse-icon", {
											class: "dxi dxi-empty",
									  }),
							]
						),
						!this.config.collapsed
							? el(
									"div",
									{
										style: {
											...stylePadding,
											height: `calc(100% - ${this.config.headerHeight || 37}px)`,
										},
										class:
											this._getCss(true) +
											" dhx_layout-cell-content" +
											(this.config.type ? typeClass : ""),
									},
									this.config.html
										? [
												el("div", {
													".innerHTML": this.config.html,
													class: "dhx_layout-cell dhx_layout-cell-inner_html",
												}),
										  ]
										: kids
							  )
							: null,
				  ]
				: this.config.html &&
				  !(
						(this.config as ILayoutConfig).rows &&
						(this.config as ILayoutConfig).cols &&
						(this.config as ILayoutConfig).views
				  )
				? [
						!this.config.collapsed
							? this.scrollView && this.scrollView.config.enable
								? this.scrollView.render([htmlContent], this._uid)
								: htmlContent
							: null,
				  ]
				: kids
		);

		return resizer ? [cell, resizer] : cell;
	}
}
